import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  TierProgramCell,
  TierProgramCellUpsertInput,
  TierProgramDetail,
} from "@colab/shared";

export type SetCellInput = {
  programId: string;
  input: TierProgramCellUpsertInput; // { row_id, tier_id, value }
};

type SetCellResponse = { ok: true; cell: TierProgramCell };

type Context = {
  previousDetail: TierProgramDetail | undefined;
};

// PUT /api/admin/tiers/programs/:programId/cells
//
// Optimistic — Pattern C state-flip. The marquee optimistic surface for the
// admin section: matrix cell free-text edits and clears land instantly on
// the local cache, then the server upsert + onSettled refetch reconciles.
//
// The cache shape stores cells as a flat list keyed by (row_id, tier_id);
// we either replace the existing row or append a synthetic one. The
// synthetic gets `id = "temp-<uuid>"` so a downstream caller that keys off
// id can detect the placeholder until the refetch returns the real id.
export function useSetTierCell() {
  const queryClient = useQueryClient();
  return useMutation<SetCellResponse, ApiClientError, SetCellInput, Context>({
    mutationFn: ({ programId, input }) =>
      apiFetch<SetCellResponse>(
        `/admin/tiers/programs/${programId}/cells`,
        {
          method: "PUT",
          body: JSON.stringify(input),
        },
      ),
    onMutate: async ({ programId, input }) => {
      const key = queryKeys.tiers.program(programId);
      await queryClient.cancelQueries({ queryKey: key });
      const previousDetail = queryClient.getQueryData<TierProgramDetail>(key);
      queryClient.setQueryData<TierProgramDetail>(key, (old) => {
        if (!old) return old;
        const idx = old.cells.findIndex(
          (c) => c.row_id === input.row_id && c.tier_id === input.tier_id,
        );
        if (idx === -1) {
          if (input.value === null) return old; // nothing to insert for a clear-on-missing
          const synthetic: TierProgramCell = {
            id: `temp-${crypto.randomUUID()}`,
            row_id: input.row_id,
            tier_id: input.tier_id,
            value: input.value,
          };
          return { ...old, cells: [...old.cells, synthetic] };
        }
        const next = old.cells.slice();
        next[idx] = { ...next[idx], value: input.value };
        return { ...old, cells: next };
      });
      return { previousDetail };
    },
    onError: (err, { programId }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.tiers.program(programId),
          context.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to save cell");
    },
    onSettled: (_data, _err, { programId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tiers.program(programId),
      });
    },
  });
}
